/**
 * The attested collections rollout switch (ADR-042), on the Dev Console's
 * Arche Management page beside the ADR-034 switch.
 *
 *   off    no tenant may create an attested log
 *   pilot  only the listed apps may (and their Forge test tenants)
 *   on     any app may — the dev sandbox and builder previews included
 *
 * CREATION-ONLY, like the app roles switch: an app that already holds a log
 * keeps it, keeps writing to it and keeps publishing it whatever this says —
 * turning it off must never strand a log. Admitting more asks first, and says
 * what it cannot undo: a live log is permanent. Admitting less is one click.
 * The server reads off when the setting is missing or unreadable.
 *
 * Server state lives in React Query under
 * `queryKeys.adminArcheGovernance.attestedCollectionsRollout()`; the mutation
 * writes the server's answer back into that cache.
 */

import { useEffect, useState } from 'react';
import { ScrollText } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import api, {
  type AttestedCollectionsRolloutDto,
  type AttestedCollectionsRolloutMode,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const ARCHE_ID = /^arc_[a-f0-9]{32}$/;
const PILOT_MAX = 50;

const MODES: Array<{ mode: AttestedCollectionsRolloutMode; label: string; meaning: string }> = [
  {
    mode: 'off',
    label: 'Off',
    meaning:
      'No app can create an attested log. Apps that already hold one keep it and keep writing to it.',
  },
  {
    mode: 'pilot',
    label: 'Pilot',
    meaning:
      'Only the apps listed below (and their Forge test channels) can create an attested log. The dev sandbox and builder previews cannot.',
  },
  {
    mode: 'on',
    label: 'On',
    meaning: 'Any app can create an attested log, in every tenant.',
  },
];

const RANK: Record<AttestedCollectionsRolloutMode, number> = { off: 0, pilot: 1, on: 2 };

/** Does `next` let an app create a log that `current` would not? */
function admitsMore(
  current: AttestedCollectionsRolloutDto,
  next: AttestedCollectionsRolloutDto,
): boolean {
  if (RANK[next.mode] !== RANK[current.mode]) return RANK[next.mode] > RANK[current.mode];
  if (next.mode !== 'pilot') return false;
  return next.pilotArcheIds.some((id) => !current.pilotArcheIds.includes(id));
}

function parsePilot(text: string): { ids: string[]; invalid: string[] } {
  const tokens = text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    ids: [...new Set(tokens.filter((t) => ARCHE_ID.test(t)))],
    invalid: tokens.filter((t) => !ARCHE_ID.test(t)),
  };
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

export function AttestedCollectionsRolloutCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const rollout = useQuery({
    queryKey: queryKeys.adminArcheGovernance.attestedCollectionsRollout(),
    queryFn: () => api.adminGetAttestedCollectionsRollout(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (next: AttestedCollectionsRolloutDto) =>
      api.adminSetAttestedCollectionsRollout(next),
    onSuccess: (result) => {
      qc.setQueryData(queryKeys.adminArcheGovernance.attestedCollectionsRollout(), result);
      toast({ title: `Attested logs: ${result.mode}` });
    },
    onError: (err) =>
      toast({
        title: 'Could not change attested logs',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });

  const [mode, setMode] = useState<AttestedCollectionsRolloutMode>('off');
  const [pilotText, setPilotText] = useState('');
  const [confirming, setConfirming] = useState<AttestedCollectionsRolloutDto | null>(null);
  const current = rollout.data;
  useEffect(() => {
    if (!current) return;
    setMode(current.mode);
    setPilotText(current.pilotArcheIds.join('\n'));
  }, [current]);

  const pilot = parsePilot(pilotText);
  const next: AttestedCollectionsRolloutDto = { mode, pilotArcheIds: pilot.ids };
  const unchanged =
    !!current &&
    current.mode === next.mode &&
    current.pilotArcheIds.join(',') === next.pilotArcheIds.join(',');
  const blocked =
    pilot.invalid.length > 0 ||
    pilot.ids.length > PILOT_MAX ||
    (mode === 'pilot' && pilot.ids.length === 0);

  function submit(): void {
    if (!current) return;
    // Admitting more asks first; admitting less is one click.
    if (admitsMore(current, next)) setConfirming(next);
    else save.mutate(next);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ScrollText className="h-4 w-4" aria-hidden="true" />
          Attested logs (ADR-042)
        </CardTitle>
        <CardDescription>
          Gates only creating a NEW attested log, in every tenant. Turning it off never touches a
          log that exists: the app keeps writing to it and keeps publishing it. Off is the safe
          reading: the server treats a missing or unreadable setting as off.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {rollout.isError ? (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-destructive">
            <p>Could not load the attested logs rollout: {errorMessage(rollout.error)}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => void rollout.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : !current ? (
          <div
            className="h-5 w-48 animate-pulse rounded bg-muted"
            aria-label="Loading attested logs"
          />
        ) : (
          <>
            <p>
              Attested logs are{' '}
              <span data-testid="attested-collections-rollout-status" className="font-semibold">
                {MODES.find((m) => m.mode === current.mode)?.label}
              </span>
              {current.mode === 'pilot' && ` for ${current.pilotArcheIds.length} app(s)`}.
            </p>
            <div role="radiogroup" aria-label="Attested logs rollout" className="flex gap-2">
              {MODES.map((m) => (
                <Button
                  key={m.mode}
                  role="radio"
                  aria-checked={mode === m.mode}
                  size="sm"
                  variant={mode === m.mode ? 'default' : 'outline'}
                  disabled={save.isPending}
                  onClick={() => setMode(m.mode)}
                >
                  {m.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {MODES.find((m) => m.mode === mode)?.meaning}
            </p>
            {mode === 'pilot' && (
              <div className="space-y-1">
                <Label htmlFor="attested-collections-pilot">
                  Pilot apps (arche ids, one per line)
                </Label>
                <Textarea
                  id="attested-collections-pilot"
                  value={pilotText}
                  onChange={(e) => setPilotText(e.target.value)}
                  rows={4}
                  placeholder="arc_…"
                  disabled={save.isPending}
                />
                {pilot.invalid.length > 0 && (
                  <p className="text-xs text-destructive">
                    Not an arche id: {pilot.invalid.slice(0, 3).join(', ')}
                  </p>
                )}
                {pilot.ids.length > PILOT_MAX && (
                  <p className="text-xs text-destructive">At most {PILOT_MAX} pilot apps.</p>
                )}
              </div>
            )}
            <Button size="sm" disabled={unchanged || blocked || save.isPending} onClick={submit}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Other servers pick a change up within about 30 seconds. Every change is audited.
            </p>
          </>
        )}
      </CardContent>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Let more apps create attested logs?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.mode === 'on'
                ? 'Any app will be able to create an attested log.'
                : `The ${confirming?.pilotArcheIds.length ?? 0} listed app(s) will be able to create an attested log.`}{' '}
              A live log is permanent: it can never be deleted, and once one exists the platform can
              never roll back below the code that keeps it. Turning this off later stops new logs
              only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const value = confirming;
                setConfirming(null);
                if (value) save.mutate(value);
              }}
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
